import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress,
} from "@mui/material";
import TerminView from "./TerminView";
import { TerminViewConstants } from "../TerminView";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import useStore from "../../../helpers/useStore";
import { AppointmentDto, RxDto, RxDtoProcessingStatusEnum } from "../../../api-client";
import notificationStore from "../../../stores/Notification/notificationStore";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { TransactionsContext } from "../../../stores/Transactions/Transactions.provider";
import { useUpdateRxProcessingStatus } from "../../../stores/Rx/rx.mutations";

interface RxStatusNotificationProps {
  open: boolean;
  selectedStatus?: RxDtoProcessingStatusEnum;
  onClose: () => void;
  rxData: RxDto;
  patientId: number;
}

const RxStatusNotification: React.FC<RxStatusNotificationProps> = ({
  open,
  selectedStatus,
  onClose,
  rxData,
  patientId,
}) => {
  const [appointments, setAppointments] = useState<AppointmentDto[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
  const [dataRefreshFlag, setDataRefreshFlag] = useState(false);
  const [openResponseDialog, setResponseDialog] = useState(false);
  const AppointmentStore = useStore(AppointmentContext);
  const { getUnattendedAppointmentsForRx } = AppointmentStore;
  const TransactionStore = useStore(TransactionsContext);
  const { getDuePaymentByRxId } = TransactionStore;
  const updateProcessingStatusMutation = useUpdateRxProcessingStatus();
  const PatientStore = useStore(PatientsContext);
  const [copaymentMessage, setCopaymentMessage] = useState<string>("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [copaymentLoading, setCopaymentLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setAppointmentsLoading(true);
        if (rxData.id) {
          const appointments = await getUnattendedAppointmentsForRx(rxData.id as string);
          setAppointments(appointments);
          setSelectedAppointments(appointments.map((appt: any) => appt.id));
        }
      } catch (error) {
        // console.log("Error", error);
      } finally {
        setAppointmentsLoading(false);
      }
    };
    fetchData();
  }, [rxData]);

  const roundToNearestHundreth = (num: number): number => {
    return Math.ceil(num * 100) / 100;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setCopaymentLoading(true);
        const dueOrOverpayed = await getDuePaymentByRxId(rxData.id as string);

        if (dueOrOverpayed) {
          const { dueAmount } = dueOrOverpayed;

          if (dueAmount > 0) {
            // Patient owes money
            setCopaymentMessage(
              `Der Patient muss noch einen Betrag von ${dueAmount.toFixed(2)} € bezahlen.`
            );
          } else if (dueAmount < 0) {
            // Patient has overpaid
            setCopaymentMessage(
              `Ein Betrag von ${Math.abs(dueAmount).toFixed(
                2
              )} € wird an den Patienten zurückerstattet.`
            );
          } else {
            // No payment needed
            setCopaymentMessage(
              "Es wurden keine Zuzahlungen geleistet, daher sind keine weiteren Abrechnungsschritte erforderlich."
            );
          }
        } else {
          // Handle unexpected case where dueOrOverpayed is null/undefined
          setCopaymentMessage(
            "Der Zuzahlungsstatus konnte nicht ermittelt werden. Bitte versuchen Sie es später erneut."
          );
        }
      } catch (error) {
        console.error("Fehler beim Abrufen des Zuzahlungsstatus:", error);
        setCopaymentMessage(
          "Der Zuzahlungsstatus konnte nicht ermittelt werden. Bitte versuchen Sie es später erneut."
        );
      } finally {
        setCopaymentLoading(false);
      }
    };

    if (openResponseDialog) {
      fetchData();
    }
  }, [openResponseDialog]);

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      // const response = await updateprocessingStatus(
      //   rxData.id as string,
      //   RxDtoProcessingStatusEnum.Abgebrochen
      // );
        if (!selectedStatus) {
          notificationStore.showMessage("Kein Status ausgewählt", "error");
          return;
        }
        const response:any =await updateProcessingStatusMutation.mutate({
            rxId: rxData.id!,
            status: selectedStatus,
            appointmentIds: selectedAppointments,
            patientId: patientId!,
          });
      // setPatientDataRefreshFlag(!patientDataRefreshFlag);
      if (response) {
        setResponseDialog(!openResponseDialog);
      }
      onClose();
    } catch (error) {
      notificationStore.showMessage("Fehler beim Löschen der Termine", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCloseResponseDialog = () => {
    setResponseDialog(false);
  };
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bestätigung erforderlich</DialogTitle>
        <DialogContent>
          <Box sx={{ margin: "10px", fontSize: "14px" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              Der Rezeptstatus wird auf "{selectedStatus ?? "unbekannt"}" aktualisiert. Sind Sie sicher, dass Sie
              aktualisieren?
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ marginTop: "10px", fontWeight: "bold", fontSize: "1.1rem" }}
            >
              Diese Aktion löscht alle nicht eingeplanten oder nicht wahrgenommenen zukünftigen
              Termine.
            </Typography>
          </Box>

          {/* List unattended appointments with checkboxes */}
          <Box sx={{ height: "500px", overflowY: "auto" }}>
            {appointmentsLoading ? (
              <CircularProgress />
            ) : (
              <>
                <TerminView
                  appointments={appointments}
                  patientId={patientId}
                  appointmentsLoading={appointmentsLoading}
                  isView={TerminViewConstants.ALL_APPOINTMENTS}
                  isSelectionMode={true}
                  selectedAppointments={selectedAppointments}
                  setSelectedAppointments={setSelectedAppointments}
                />
              </>
            )}
          </Box>

          <Typography variant="body1" mt={2}>
            {appointments &&
              appointments.length > 0 &&
              "Die Zuzahlung muss möglicherweise jetzt angepasst werden."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose} disabled={saveLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saveLoading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openResponseDialog} onClose={handleCloseResponseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Zuzahlungsstatus</DialogTitle>
        <DialogContent>
          {copaymentLoading ? (
            <CircularProgress />
          ) : (
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "1.25rem",
                textAlign: "center",
                p: 2,
              }}
            >
              {copaymentMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResponseDialog} variant="text">
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RxStatusNotification;
